import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import React, { Dispatch, FormEventHandler, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormSetValue, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { GetPsCheckDownloadParams } from '../../../../../api/model/getPsCheckDownloadParams';
import { GetPsCheckParams } from '../../../../../api/model/getPsCheckParams';
import { PsCheckUpdate } from '../../../../../api/model/psCheckUpdate';
import {
  GetPsCheckDownloadQueryResult,
  useGetPsCheck,
  useGetPsCheckDownload,
  usePostPsCheck,
} from '../../../../../api/ps-checks/ps-checks';
import { useDefaultOptions, useOptionsForBinary } from '../../../../../hooks/api';
import yup from '../../../../../utils/custom-yup';
import { executeDownload } from '../../../../../utils/download';
import FileDrop from '../../../../atoms/FileDrop';
import NoDataComponent from '../../../../atoms/NoDataComponent';
import BlobErrorModal from '../../../../modules/BlobErrorModal';
import ConfirmModal from '../../../../modules/ConfirmModal';
import ErrorModal from '../../../../modules/ErrorModal';
import Modal from '../../../../modules/Modal';
import OkModal from '../../../../modules/OkModal';
import SpinnerModal from '../../../../modules/SpinnerModal';
import { DialogType, Message, getDialogMessage } from '../PsListCommmon';

interface Props {
  onClose: () => void;
  displayCloseButton: boolean;
  versionId: number;
  state: GetPsCheckParams | undefined;
  setActiveModal: Dispatch<SetStateAction<number>>;
  isViewMode: boolean;
  setRefresh: () => void;
  setMessage: Dispatch<SetStateAction<string>>;
}

/**
 * 各入力項目定数化
 */
const PREPARATION_ITEM_INPUT_NAMES = {
  changeRequestSection: 'change_request_section',
  releaseNote: 'release_note',
  message: 'message',
  fgImg: 'fg_img',
};

/**
 * HM提出ダイアログ
 */
function PsHmSubmitDialog({
  onClose,
  displayCloseButton,
  versionId,
  state,
  setActiveModal,
  isViewMode,
  setRefresh,
  setMessage,
}: Props) {
  // 翻訳用定数
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  const [confirmDalogFlg, setConfirmDalogFlg] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [displayValidationError, setDisplayValidationError] = useState<boolean>(false);
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);
  const [saveErrorModal, setSaveErrorModal] = useState(false);
  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  const body = useRef<PsCheckUpdate>();
  const dialogMessage = useRef('');

  // ダイアログ情報取得用param準備
  const param = isViewMode
    ? ({ ps_temporary_state_history_id: state?.ps_temporary_state_history_id } as GetPsCheckParams)
    : ({} as GetPsCheckParams);

  // 入力項目 バリデーション
  const schema = yup.object({
    [PREPARATION_ITEM_INPUT_NAMES.changeRequestSection]: yup.string().nullable(),
    [PREPARATION_ITEM_INPUT_NAMES.releaseNote]: yup.string().required(),
    [PREPARATION_ITEM_INPUT_NAMES.message]: yup.string().nullable(),
  });

  // PS改廃案情報表示(HM提出情報含む)取得
  const {
    data: psCheckData,
    isLoading: psCheckIsLoading,
    isError: psCheckIsError,
    error: psCheckError,
  } = useGetPsCheck(versionId, param, options);

  const { handleSubmit, setValue, register } = useForm({
    defaultValues: psCheckData?.data,
    resolver: yupResolver(schema),
  });

  /**
   * ボタン押下時、確認ダイアログの表示
   */
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      // バリデーション結果が無効だった場合（エラーモーダルを表示）
      e.preventDefault();
      setIsSubmitting(true);

      // 成功時
      const onValid = (data: FieldValues) => {
        // bodyに設定する各プロパティを保持
        body.current = { ...data } as PsCheckUpdate;
        if (data.files?.length > 0) {
          // ファイル
          body.current['files[]'] = data.files as Blob[];
        }
        dialogMessage.current = getDialogMessage(Message.CONFIRM_HM_SUBMIT_PS_TEMPORARY, t);
        setIsSubmitting(false);

        // 確認ダイアログの表示
        setConfirmDalogFlg(true);
      };

      // 失敗時
      const onInvalid = () => {
        setDisplayValidationError(true);
        dialogMessage.current = getDialogMessage(Message.VALIDATE_INPUT_RELEASE_NOTE, t);
      };

      void handleSubmit(onValid, onInvalid)();
    },
    [handleSubmit, t]
  );

  /**
   * 確認ダイアログOK押下時、各API実行
   * @param bodyObject
   * @returns
   */
  const DialogOkClick = async () => {
    if (isSubmitting) {
      return;
    }
    setDisplaySpinnerModal(true);
    setIsSubmitting(true);
    await save();
  };

  // HM提出API
  const { mutateAsync: saveAsync, error: saveError } = usePostPsCheck(options);

  // 添付ファイルダウンロードクリック時
  const paramForDownlad = isViewMode
    ? ({ ps_temporary_state_history_id: state?.ps_temporary_state_history_id } as GetPsCheckDownloadParams)
    : ({} as GetPsCheckDownloadParams);
  const optionsForBinary = useOptionsForBinary(language);
  const { refetch, isError, error } = useGetPsCheckDownload<GetPsCheckDownloadQueryResult, AxiosError<Blob>>(
    versionId,
    paramForDownlad,
    optionsForBinary
  );
  const onPsCheckDownloadClicked = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await refetch();
    setDisplaySpinnerModal(false);
    if (response.data != null) {
      executeDownload(response.data);
    }
  }, [refetch]);

  useEffect(() => setDisplayDownloadError(isError), [isError]);

  // HM提出成功時
  const onSuccessSave = useCallback(() => {
    // 保存成功したらリフェッチする
    setIsSubmitting(false);
    setConfirmDalogFlg(false);
    setMessage(getDialogMessage(Message.SUCCESS_HM_SUBMIT_PS_TEMPORARY, t));
    setActiveModal(DialogType.MESSAGE);
    setValue('attach_files', []);
    setRefresh();
  }, [setActiveModal, setMessage, setRefresh, setValue, t]);

  // 保存失敗時
  const onErrorSave = useCallback(() => {
    setDisplaySpinnerModal(false);
    setConfirmDalogFlg(false);
    setSaveErrorModal(true);
    setIsSubmitting(false);
    setValue('attach_files', []);
  }, [setValue]);

  // 保存する
  const save = useCallback(async () => {
    const psVersionId = versionId;
    // 保存実行
    await saveAsync(
      {
        psVersionId,
        data: body?.current ?? {},
      },
      { onSuccess: onSuccessSave, onError: onErrorSave }
    );
  }, [saveAsync, versionId, body, onSuccessSave, onErrorSave]);

  // ローディング
  if (psCheckIsLoading) {
    return <SpinnerModal />;
  }

  // エラー時
  if (psCheckIsError) {
    return <ErrorModal onClickOk={onClose} error={psCheckError} />;
  }

  // データがない場合
  if (psCheckData?.data == null) {
    return <NoDataComponent />;
  }

  return (
    <>
      <Modal active onClose={onClose} displayCloseButton={displayCloseButton} additionalClassName="popupfr1030">
        <form onSubmit={onSubmit}>
          <div className="pop-tablefr1">
            <div>
              <table>
                {/* HM提出ダイアログ 画面 1列目 */}
                <tbody>
                  <tr>
                    <th>{t('管理No.')}</th>
                    <td>{psCheckData.data.ps_no}</td>
                  </tr>
                  <tr>
                    <th>{t('構成')}</th>
                    <td>
                      <div>
                        {/* [構成]は配列のオブジェクト 改行して1つにまとめる */}
                        {psCheckData.data.composition?.map((val, index) => {
                          if (index === psCheckData.data.composition?.length ?? 0 - 1) {
                            return <React.Fragment key={val}>{val}</React.Fragment>;
                          }
                          return (
                            <React.Fragment key={val}>
                              {val}
                              <br />
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <table>
                {/* HM提出ダイアログ 画面 2列目 */}
                <tbody>
                  <tr>
                    <th>{t('テキスト確定条件')}</th>
                    <td>{psCheckData.data.text_condition}</td>
                  </tr>
                  <tr>
                    <th>{t('タイトル')}</th>
                    <td>{psCheckData.data.title}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="pop-tablefr2">
            <table className="poptable1">
              <tbody>
                <tr className="pop-table-tr-line">
                  <th>
                    <label htmlFor="changeLaunchSection">{t('変更打上部門')}</label>
                  </th>
                  <td>
                    {/* 編集可能 ただし閲覧モードの場合、表示のみ */}
                    {!isViewMode ? (
                      <input
                        type="text"
                        id="changeLaunchSection"
                        defaultValue={psCheckData.data.change_request_section}
                        {...register('change_request_section')}
                      />
                    ) : (
                      <label>{psCheckData.data.change_request_section}</label>
                    )}
                  </td>
                </tr>
                <tr className="pop-table-tr-line">
                  <th>
                    <label htmlFor="release_note">{t('変更の理由')}</label>
                    {!isViewMode ? <span className="req">{t('必須')}</span> : ''}
                  </th>
                  <td>
                    {/* 編集可能 ただし閲覧モードの場合、表示のみ */}
                    {!isViewMode ? (
                      <textarea
                        id="release_note"
                        {...register('release_note')}
                        rows={5}
                        cols={33}
                        defaultValue={psCheckData.data.release_note}
                      />
                    ) : (
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: psCheckData.data.release_note == null ? "" : psCheckData.data.release_note.replace(/\r\n|\n/g, '<br />')}}
                      />
                    )}
                  </td>
                </tr>
                <tr className="pop-table-tr-line">
                  <th>
                    <label htmlFor="message">{t('申請の理由')}</label>
                  </th>
                  <td>
                    {!isViewMode ? (
                      <textarea
                        id="message"
                        rows={5}
                        cols={33}
                        defaultValue={psCheckData.data.message}
                        {...register('message')}
                      />
                    ) : (
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: psCheckData.data.message == null ? "" : psCheckData.data.message.replace(/\r\n|\n/g, '<br />')}}
                      />
                  )}
                  </td>
                </tr>
                <tr className="pop-table-tr-multi-line non-bottom-input-tr">
                  <th>
                    <label htmlFor="upFileWrap">{t('添付ファイル')}</label>
                  </th>
                  <td>
                    {/* 添付ファイルは閲覧モードの場合表示のみ */}
                    {isViewMode ? (
                      <label>{psCheckData.data.attach_files?.map((file) => file.attach_file_name).join(',')}</label>
                    ) : (
                      <div>
                        <FileDrop id="files" name="files" setValue={setValue as UseFormSetValue<FieldValues>} />
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* フッター部 */}
          <div className="btnfr-all mt20 bfall1 conf-req-btn1fr">
            <div className="btnfr-left1">
              <ul className="ps-dialogbtn" key="0">
                <li key="1">
                  <Link
                    to={`/${language}/ps/${psCheckData.data.ps_type === 0 || psCheckData.data.ps_type === 1 ? 'difftree' : 'diff'}/${psCheckData.data.newest_ps_version_id?.toString() ?? ''}/${
                      psCheckData.data.ps_version_id?.toString() ?? ''
                    }`}
                    target="_blank"
                  >
                    <i className="fa-solid fa-eye" />
                    {t('PS改廃案表示')}
                  </Link>
                </li>
                <li key="2">
                  <Link
                    to={`/${language}/ps/view/${psCheckData.data.newest_ps_version_id?.toString() ?? ''}`}
                    target="_blank"
                  >
                    <i className="fa-solid fa-eye" />
                    {t('最新PS表示')}
                  </Link>
                </li>
                {/* 添付ファイルは閲覧モードの場合、アイコンを表示しダウンロードできるようにする */}
                {(psCheckData.data.attach_files?.length ?? -1) > 0 && (
                  <button type="button" className="text-button" onClick={onPsCheckDownloadClicked}>
                    <li className="cursor">
                      <i className="fa-solid fa-paperclip" />
                      {t('添付ファイル')}
                    </li>
                  </button>
                )}
              </ul>
            </div>
            <div className="btnfr-right1">
              <div className="btnfr btnfr4">
                <div>
                  <button
                    type="button"
                    className="btn2 btnbg-g text-button"
                    onClick={() => {
                      if (!isViewMode) {
                        onClose();
                      } else {
                        setActiveModal(DialogType.STATE_HISTORY);
                      }
                    }}
                  >
                    <span className="modal-close2">{!isViewMode ? t('キャンセル') : t('閉じる')}</span>
                  </button>
                  {/* HM提出ボタンは閲覧モードの場合非表示 */}
                  {!isViewMode ? (
                    <span className="modal-open">
                      <input type="submit" id="submit" name="submit" value={t('HM提出').toString()} className="btn1" />
                    </span>
                  ) : (
                    ''
                  )}{' '}
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal>
      {/* 確認メッセージ */}
      {confirmDalogFlg && (
        <ConfirmModal
          onClickOk={() => {
            void DialogOkClick();
          }}
          onClickCancel={() => {
            setConfirmDalogFlg(false);
          }}
        >
          <div className="poptxt">
            <p>{dialogMessage.current}</p>
          </div>
        </ConfirmModal>
      )}
      {/* スピナーモーダル */}
      {displaySpinnerModal && <SpinnerModal />}
      {/* バリデーションエラーモーダル */}
      {displayValidationError && (
        <OkModal onClickOk={() => setDisplayValidationError(false)}>{dialogMessage.current}</OkModal>
      )}
      {/* HM提出エラーの表示 */}
      {saveErrorModal && <ErrorModal onClickOk={() => setSaveErrorModal(false)} error={saveError}>{getDialogMessage(Message.FAILURE_HM_SUBMIT_PS_TEMPORARY, t)}</ErrorModal>}
      {/* ダウンロードエラー */}
      {displayDownloadError && <BlobErrorModal error={error} onClickOk={() => setDisplayDownloadError(false)} />}
    </>
  );
}

export default PsHmSubmitDialog;
